import React, { useMemo } from 'react';
import { useGetAnnouncementsQuery } from '../redux/announcementApi';
import { getStoredAuthToken } from '../utils/authFetch';
import { isProbablyHtml, stripDangerousHtml } from '../utils/notificationHtml';

function isShownAnnouncement(item) {
  const s = item?.status;
  if (s === false || s === 0) return false;
  if (typeof s === 'string') {
    const t = s.toLowerCase();
    if (['no', 'false', 'inactive', '0', 'disabled'].includes(t)) return false;
  }
  return true;
}

function cleanDescription(value) {
  return String(value || '')
    .replace(/\r\n/g, ' ')
    .replace(/\n/g, ' ')
    .replace(/\r/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function prepareDescription(value) {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  if (isProbablyHtml(raw)) return stripDangerousHtml(raw);
  return cleanDescription(raw);
}

function normalizeAnnouncements(res) {
  let raw = res?.data ?? res;
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'object') {
    if (Array.isArray(raw.announcements)) return raw.announcements;
    if (Array.isArray(raw.records)) return raw.records;
    if (
      'id' in raw ||
      'title' in raw ||
      'Id' in raw ||
      'Title' in raw
    ) {
      return [raw];
    }
  }
  return [];
}

/** Marquee under main nav: description only; each slide is full strip width so motion is right → left. */
function HeaderAnnouncementTicker() {
  const hasToken = Boolean(getStoredAuthToken());
  const { data, isLoading, isError } = useGetAnnouncementsQuery(undefined, {
    skip: !hasToken,
  });

  const tickerText = useMemo(() => {
    const list = normalizeAnnouncements(data).filter(isShownAnnouncement);
    const parts = list
      .map((a) => prepareDescription(a.description ?? a.Description ?? ''))
      .filter(Boolean);
    return parts.join('     ···     ');
  }, [data]);

  if (isError || (!isLoading && !tickerText)) return null;

  const slideContent = (ariaHidden) => (
    <span
      className="inline-block shrink-0 whitespace-nowrap px-6 text-sm font-bold leading-snug text-brand [&_b]:font-bold [&_p]:m-0 [&_p]:inline [&_strong]:font-bold"
      aria-hidden={ariaHidden || undefined}
      dangerouslySetInnerHTML={{ __html: tickerText }}
    />
  );

  return (
    <div className="border-t border-gray-100 bg-gray-50/60">
      <div className="w-full overflow-hidden py-2">
        {isLoading ? (
          <div
            className="mx-auto h-4 w-48 max-w-full rounded bg-gray-200/80"
            aria-hidden
          />
        ) : (
          <div
            className="flex w-max animate-landing-marquee hover:[animation-play-state:paused]"
            role="region"
            aria-label="Announcement"
          >
            {slideContent(false)}
            {slideContent(true)}
          </div>
        )}
      </div>
    </div>
  );
}

export default HeaderAnnouncementTicker;
